import { useState, useEffect } from 'react';
import { Stack, Container, Group, Loader, Alert, Divider, ScrollArea } from '@mantine/core';
import { useSchemaForm } from '@ayocore/platform/client/hooks/useSchemaForm';
import partnerSchemaDefinition, { type Partner } from '../../../../schemas/partner.schema';
import { usePartnerRepository } from '@ayocore/erp/crm/client/rpc';
import { userRepository } from '@ayocore/platform/identity/client/rpc';
import logger from '@ayocore/platform/common/helpers/logger';

/**
 * EditPartnerForm - Partner editing form with clean setInitialData approach
 */
export interface EditPartnerFormProps extends Record<string, unknown> {
  partnerId: number;
  onPartnerUpdated?: () => void;
}

export function EditPartnerForm({ partnerId, onPartnerUpdated }: EditPartnerFormProps) {
  const partnerRepository = usePartnerRepository();
  const [initialData, setInitialData] = useState<Partner | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Initialize SchemaForm with stable empty values
  const SchemaForm = useSchemaForm(partnerSchemaDefinition.updateSchema, {
    hideDescriptions: true,
  });

  // Update form values when data loads - clean approach!
  useEffect(() => {
    if (initialData) {
      SchemaForm.setInitialData(initialData);
    }
  }, [initialData]);

  // Load existing partner data
  useEffect(() => {
    const loadPartner = async () => {
      try {
        setLoading(true);
        setLoadError(null);

        const partner = await partnerRepository.findById(partnerId);

        if (!partner) {
          setLoadError(`Partner with ID ${partnerId} not found`);
          return;
        }

        setInitialData(partner);
      } catch (error) {
        logger.error('Failed to load partner:', error);
        setLoadError(error instanceof Error ? error.message : 'Failed to load partner');
      } finally {
        setLoading(false);
      }
    };

    if (partnerId) {
      loadPartner();
    }
  }, [partnerId]);

  const handleSubmit = async (values: typeof SchemaForm.values) => {
    // Update partner using repository
    const updatedPartner = await partnerRepository.update(partnerId, values);

    // Show success message
    SchemaForm.setSuccess(`🏢 Партнер "${updatedPartner.name}" успешно обновлен!`);

    // Call the callback to refresh list and close modal after a brief delay
    setTimeout(() => {
      if (onPartnerUpdated) {
        onPartnerUpdated();
      }
    }, 1500); // Allow user to see success message

    return { success: true };
  };

  // Loading state
  if (loading) {
    return (
      <Container size='sm' py='xl'>
        <Group justify='center'>
          <Loader size='sm' />
          <span>Loading partner...</span>
        </Group>
      </Container>
    );
  }

  // Error state
  if (loadError) {
    return (
      <Container size='sm' py='xl'>
        <Alert color='red' title='Ошибка'>
          {loadError}
        </Alert>
      </Container>
    );
  }

  // Don't render form until data is loaded
  if (!initialData) {
    return (
      <Container size='sm' py='xl'>
        <Group justify='center'>
          <Loader size='sm' />
          <span>Loading partner data...</span>
        </Group>
      </Container>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '80dvh' }}>
      {/* Scrollable Form Content */}
      <ScrollArea style={{ flex: 1 }} scrollHideDelay={0}>
        <Container size='sm' px={{ base: 'xs', sm: 'sm' }} py='xs' maw='100%'>
          <form onSubmit={SchemaForm.handleSubmit(handleSubmit)}>
            <Stack gap='xs' pb='md'>
              <SchemaForm.Messages />

              <SchemaForm.Field.name size='sm' />
              <SchemaForm.Field.full_name size='sm' />
              <SchemaForm.Field.manager_id
                size='sm'
                label='Менеджер'
                repo={userRepository}
                displayField='name'
                placeholder='Выберите менеджера...'
              />

              <Divider />

              {/* Notes */}
              <SchemaForm.Field.notes size='sm' />
            </Stack>

            {/* Sticky Footer with Action Buttons - INSIDE FORM */}
            <div
              style={{
                borderTop: '1px solid #e9ecef',
                backgroundColor: 'transparent',
                padding: '8px 0 8px 12px',
                flexShrink: 0,
                position: 'sticky',
                bottom: 0,
              }}
            >
              <Group justify='flex-end'>
                <SchemaForm.buttons.cancel
                  onClick={() => onPartnerUpdated?.()}
                  label='Отмена'
                  size='sm'
                />
                <SchemaForm.buttons.submit label='Сохранить' size='sm' />
              </Group>
            </div>
          </form>
        </Container>
      </ScrollArea>
    </div>
  );
}

export default EditPartnerForm;
